/**
 * AURA Accessibility Statement Modal
 * Displays the accessibility statement configured by the host application
 */

import { AuraConfig, StatementConfig } from '../core/types';
import { t } from '../core/i18n';
import { getIcon } from './styles/icons';

export const STATEMENT_MODAL_STYLES = `
  .aura-statement-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: calc(var(--aura-z-panel) + 1);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    pointer-events: none;
    transition: opacity var(--aura-transition);
  }

  .aura-statement-overlay--visible {
    opacity: 1;
    pointer-events: auto;
  }

  .aura-statement {
    background: var(--aura-bg);
    border-radius: var(--aura-radius-lg);
    box-shadow: var(--aura-shadow-lg);
    width: 90%;
    max-width: 560px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    transform: scale(0.95);
    transition: transform var(--aura-transition);
  }

  .aura-statement-overlay--visible .aura-statement {
    transform: scale(1);
  }

  .aura-statement__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--aura-spacing-lg);
    border-bottom: 1px solid var(--aura-border);
  }

  .aura-statement__title {
    font-size: var(--aura-font-size-xl);
    font-weight: 600;
    color: var(--aura-text);
    margin: 0;
  }

  .aura-statement__close {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--aura-radius-sm);
    color: var(--aura-text-muted);
    cursor: pointer;
    background: none;
    border: none;
    transition: all var(--aura-transition-fast);
  }

  .aura-statement__close:hover {
    background: var(--aura-bg-hover);
    color: var(--aura-text);
  }

  .aura-statement__close svg {
    width: 20px;
    height: 20px;
  }

  .aura-statement__body {
    flex: 1;
    overflow-y: auto;
    padding: var(--aura-spacing-lg);
    font-size: var(--aura-font-size-md);
    color: var(--aura-text);
    line-height: 1.6;
  }

  .aura-statement__section {
    margin-bottom: var(--aura-spacing-lg);
  }

  .aura-statement__section:last-child {
    margin-bottom: 0;
  }

  .aura-statement__section-title {
    font-size: var(--aura-font-size-md);
    font-weight: 600;
    color: var(--aura-text);
    margin-bottom: var(--aura-spacing-sm);
  }

  .aura-statement__text {
    color: var(--aura-text-muted);
    margin-bottom: var(--aura-spacing-sm);
  }

  .aura-statement__link {
    color: var(--aura-secondary);
    text-decoration: none;
  }

  .aura-statement__link:hover {
    text-decoration: underline;
  }

  .aura-statement__list {
    list-style: disc;
    padding-left: var(--aura-spacing-lg);
    color: var(--aura-text-muted);
  }

  .aura-statement__list li {
    margin-bottom: var(--aura-spacing-xs);
  }
`;

export interface StatementModal {
  overlay: HTMLElement;
  open: () => void;
  close: () => void;
}

export function createStatementModal(config: AuraConfig): StatementModal {
  const overlay = document.createElement('div');
  overlay.className = 'aura-statement-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', t('statementTitle'));

  const modal = document.createElement('div');
  modal.className = 'aura-statement';

  const header = document.createElement('div');
  header.className = 'aura-statement__header';

  const title = document.createElement('h2');
  title.className = 'aura-statement__title';
  title.textContent = t('statementTitle');

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'aura-statement__close';
  closeBtn.setAttribute('aria-label', t('statementClose'));
  closeBtn.innerHTML = getIcon('close');

  header.appendChild(title);
  header.appendChild(closeBtn);

  const body = document.createElement('div');
  body.className = 'aura-statement__body';
  body.innerHTML = buildStatementContent(config.statement);

  modal.appendChild(header);
  modal.appendChild(body);
  overlay.appendChild(modal);

  const close = () => {
    overlay.classList.remove('aura-statement-overlay--visible');
  };

  const open = () => {
    overlay.classList.add('aura-statement-overlay--visible');
    closeBtn.focus();
  };

  closeBtn.addEventListener('click', close);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  overlay.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
  });

  return { overlay, open, close };
}

function buildStatementContent(statement?: StatementConfig): string {
  if (!statement) {
    return `
      <div class="aura-statement__section">
        <p class="aura-statement__text">
          This website is committed to ensuring digital accessibility for people with disabilities.
          We are continually improving the user experience for everyone, and applying the relevant
          accessibility standards.
        </p>
      </div>
      <div class="aura-statement__section">
        <h3 class="aura-statement__section-title">Conformance Status</h3>
        <p class="aura-statement__text">
          We strive to conform to WCAG 2.1 Level AA guidelines to ensure our content
          is accessible to the widest possible audience.
        </p>
      </div>
      <div class="aura-statement__section">
        <h3 class="aura-statement__section-title">Feedback</h3>
        <p class="aura-statement__text">
          We welcome your feedback on the accessibility of this website. Please let us know
          if you encounter accessibility barriers.
        </p>
      </div>
    `;
  }

  let html = '';

  html += `
    <div class="aura-statement__section">
      <p class="aura-statement__text">
        <strong>${statement.companyName}</strong> is committed to ensuring digital accessibility
        for people with disabilities. We are continually improving the user experience for
        everyone, and applying the relevant accessibility standards.
      </p>
    </div>
  `;

  if (statement.complianceTarget) {
    html += `
      <div class="aura-statement__section">
        <h3 class="aura-statement__section-title">Conformance Status</h3>
        <p class="aura-statement__text">
          We strive to conform to <strong>${statement.complianceTarget}</strong>.
        </p>
      </div>
    `;
  }

  if (statement.knownLimitations?.length) {
    html += `
      <div class="aura-statement__section">
        <h3 class="aura-statement__section-title">Known Limitations</h3>
        <ul class="aura-statement__list">
          ${statement.knownLimitations.map((l) => `<li>${l}</li>`).join('')}
        </ul>
      </div>
    `;
  }

  if (statement.additionalContent) {
    html += `
      <div class="aura-statement__section">
        ${statement.additionalContent}
      </div>
    `;
  }

  html += `
    <div class="aura-statement__section">
      <h3 class="aura-statement__section-title">Contact</h3>
      <p class="aura-statement__text">
        Email: <a href="mailto:${statement.contactEmail}" class="aura-statement__link">${statement.contactEmail}</a>
        ${statement.contactPhone ? `<br/>Phone: ${statement.contactPhone}` : ''}
        ${statement.companyUrl ? `<br/>Website: <a href="${statement.companyUrl}" class="aura-statement__link" target="_blank" rel="noopener">${statement.companyUrl}</a>` : ''}
      </p>
    </div>
  `;

  if (statement.lastReviewDate) {
    html += `
      <div class="aura-statement__section">
        <p class="aura-statement__text" style="font-style: italic;">
          This statement was last reviewed on ${statement.lastReviewDate}.
        </p>
      </div>
    `;
  }

  return html;
}
